import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Warning as WarningIcon, Close as CloseIcon, DeleteSweep } from '@mui/icons-material';
import { deleteUserData, getSubscriptionStatus } from '../api/apiClient';
import { setMonthlyExpensesCache } from '../utils/monthlyExpenses';

const DELETION_REASONS = [
  { code: 'no_longer_needed', label: 'I no longer need to store receipts' },
  { code: 'switched_apps', label: 'I moved to another expense tool' },
  { code: 'privacy_concerns', label: 'I want my personal data removed' },
  { code: 'data_accuracy', label: 'Receipt data isn’t accurate enough' },
  { code: 'technical_issues', label: 'I’m facing technical issues / bugs' },
  { code: 'cleanup_request', label: 'I want to start fresh with a clean slate' },
  { code: 'other', label: 'Other' },
];

const DeleteDataModal = ({ open, onClose, user, onSuccess }) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [otherText, setOtherText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setError('');

    if (!selectedReason) {
      setError('Please select a reason for deleting your data');
      return;
    }

    if (selectedReason === 'other' && !otherText.trim()) {
      setError('Please provide details for "Other" reason');
      return;
    }

    setLoading(true);
    try {
      const response = await deleteUserData(
        user?.user_id || user?.id,
        selectedReason,
        selectedReason === 'other' ? otherText.trim() : '',
        'web'
      );

      if (response.data.success) {
        try {
          const statusResponse = await getSubscriptionStatus(user?.user_id || user?.id);
          if (statusResponse?.data?.success && statusResponse?.data?.data) {
            const subscriptionData = statusResponse.data.data;
            localStorage.setItem('subscription_status', subscriptionData.status || 'free_unlimited');
            if (subscriptionData.data_deleted_at) {
              localStorage.setItem('data_deleted_at', subscriptionData.data_deleted_at);
            }
            setMonthlyExpensesCache({
              total: 0,
              totalReceipts: 0,
              categories: [],
            });
          }
        } catch (statusError) {
          console.error('Error fetching subscription status after deletion:', statusError);
        }

        onSuccess?.();
        handleClose();
        alert('Your receipts and related data are being deleted permanently.');
      } else {
        setError(response.data.error || 'Failed to delete data');
      }
    } catch (apiError) {
      console.error('Delete data error:', apiError);
      setError(apiError.response?.data?.error || 'Failed to delete data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setOtherText('');
    setError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          Delete My Data
        </Typography>
        <Button
          onClick={handleClose}
          sx={{ minWidth: 'auto', p: 0.5 }}
          disabled={loading}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3, p: 2, bgcolor: '#FFF5F5', borderRadius: 2 }}>
          <DeleteSweep sx={{ color: '#D32F2F', fontSize: 32 }} />
          <Typography variant="body2" sx={{ color: '#D32F2F' }}>
            This action permanently deletes your receipts and associated data. Your account stays active on the free plan.
          </Typography>
        </Stack>

        <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
          Why are you deleting your data?
        </Typography>

        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value)}
          >
            {DELETION_REASONS.map((reason) => (
              <FormControlLabel
                key={reason.code}
                value={reason.code}
                control={<Radio />}
                label={reason.label}
                sx={{
                  mb: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    borderRadius: 1,
                  },
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {selectedReason === 'other' && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Please provide details..."
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              variant="outlined"
              sx={{ mt: 1 }}
            />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            mt: 3,
            p: 2,
            backgroundColor: '#FFF5F5',
            borderRadius: 1,
          }}
        >
          <WarningIcon sx={{ color: '#FF6B6B', mt: 0.5 }} />
          <Typography variant="body2" sx={{ color: '#FF6B6B', lineHeight: 1.6 }}>
            Once deleted, we cannot recover your receipts or their analytics. You can continue using the app and upload new receipts anytime.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ textTransform: 'none', color: '#666' }}
        >
          Keep My Data
        </Button>
        <Button
          onClick={handleDelete}
          disabled={loading || !selectedReason}
          variant="contained"
          color="error"
          sx={{ textTransform: 'none' }}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {loading ? 'Deleting...' : 'Delete Data'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteDataModal;

